import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queries } from "../services/queries";
import Companies from "../services/Companies";

export function useCompanyCardsData(): UseQueryResult<ICompanyCard[]> {
  return useQuery({
    ...queries.companies.getCompanyCardsData,
    queryFn: () => Companies.getCompanyCardsData(),
  });
}

export function useGetCompanies(): UseQueryResult<IActiveCompanies[]> {
  return useQuery({
    ...queries.companies.getCompanies,
    queryFn: () => Companies.getCompanies(),
  });
}

export function useGetCompanyMemberData(
  id: string | number,
  pageNumber: string | number | null,
  pageSize: number | string | null = 6,
  userName?: string | null,
  subscriptionName?: string | null,
  status?: number | null,
  turnover?: boolean | null
): UseQueryResult<IPaginatedCompanyMembersResponse> {
  return useQuery({
    ...queries.companies.getCompanyMembersData(
      id,
      pageNumber,
      pageSize,
      userName,
      subscriptionName,
      status,
      turnover
    ),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });
}

export function useGetCompanyPaymentsData(
  id: string | number
): UseQueryResult<ICompanyPayments[]> {
  return useQuery({
    ...queries.companies.getCompanyPaymentsData(id),
  });
}

export function useFetchEditComapnyData(
  companyId: string | number
): UseQueryResult<IEditCompany> {
  return useQuery({
    ...queries.companies.getEditCompanyData(companyId),
  });
}

export function useAddCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Companies.addCompany,
    onSuccess: () => {
      // Delay query invalidation to show success message first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanies.queryKey });
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanyCardsData.queryKey });
        queryClient.invalidateQueries({ queryKey: ["companies", "getCompanyPaginatedCardsData"] });
      }, 500);
    },
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useCompanyRenewal() {
  return useMutation({
    mutationFn: Companies.companyRenewal,
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}
export function useDeactivateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string | undefined) =>
      Companies.deactivateCompany(companyId),
    onSuccess: () => {
      // Delay query invalidation to show success message first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanies.queryKey });
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanyCardsData.queryKey });
        queryClient.invalidateQueries({ queryKey: ["companies", "getCompanyPaginatedCardsData"] });
      }, 500);
    },
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useCancelCompanyImmediately() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string) =>
      Companies.cancelCompanyImmediately(companyId),
    onSuccess: () => {
      // Delay query invalidation to show success message first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanies.queryKey });
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanyCardsData.queryKey });
        queryClient.invalidateQueries({ queryKey: ["companies", "getCompanyPaginatedCardsData"] });
      }, 500);
    },
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useScheduleCompanyCancel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId, cancelDate }: IScheduleCancelParams) => {
      return Companies.scheduleCancelCompany(companyId, cancelDate);
    },
    onSuccess: () => {
      // Delay query invalidation to show success message first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanies.queryKey });
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanyCardsData.queryKey });
        queryClient.invalidateQueries({ queryKey: ["companies", "getCompanyPaginatedCardsData"] });
      }, 500);
    },
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useReactivateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string | null) =>
      Companies.ReactivateCompany(companyId),
    onSuccess: () => {
      // Delay query invalidation to show success message first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanies.queryKey });
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanyCardsData.queryKey });
        queryClient.invalidateQueries({ queryKey: ["companies", "getCompanyPaginatedCardsData"] });
      }, 500);
    },
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useAddCompanyMembers() {
  return useMutation({
    mutationFn: async (company: ICompanyMembers) =>
      Companies.AddCompanyMembers(company),
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useEditCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (company: IEditCompany) => Companies.editCompany(company),
    onSuccess: () => {
      // Delay query invalidation to show success message first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanies.queryKey });
        queryClient.invalidateQueries({ queryKey: queries.companies.getCompanyCardsData.queryKey });
        queryClient.invalidateQueries({ queryKey: ["companies", "getCompanyPaginatedCardsData"] });
      }, 500);
    },
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useAddCompanyPayment() {
  return useMutation({
    mutationFn: Companies.AddCompanyPayment,
    meta: {
      conflict: "Found whilst making changes",
      badRequest: "Please make sure all fields are entered correctly.",
      notFound: "Company could not be found. Please try again",
    },
  });
}

export function useGetPaginatedCompanyCardsData(
  pageNumber: string | number | null,
  pageSize: number | string | null,
  name: string | null,
  enabled: boolean = true
): UseQueryResult<ICompanyCardResponse> {
  return useQuery({
    ...queries.companies.getCompanyPaginatedCardsData(
      pageNumber,
      pageSize,
      name
    ),
    refetchOnWindowFocus: false,
    enabled, // Only fetch when enabled
  });
}

export function useGetAllCompanyCardsData(
  enabled: boolean = true
): UseQueryResult<ICompanyCard[]> {
  return useQuery({
    queryKey: ["companies", "getAllCompanyCardsData"],
    queryFn: () => Companies.getAllCompanyCardsData(),
    refetchOnWindowFocus: false,
    enabled, // Only fetch when enabled
  });
}
